import { useState, useEffect, useRef, useCallback } from 'react';
import { RoomEvent } from 'livekit-client';

/*
 * Co-browse for Avatar Panel V2.
 *
 * The same feature as the co-browse voice widget (CobrowseWidget.js): during
 * the call the agent can scroll, navigate, search and fill forms on the host
 * page. Here the call UI is the avatar panel, so this hook is the panel's half
 * of the co-browse protocol and nothing else:
 *
 *   agent (app/agent/cobrowse.py)
 *     <-- LiveKit data, topic braincx.cobrowse -->  this hook (in the panel)
 *     <-- postMessage, bcx-cobrowse-* -->           cobrowse-widget.js on the
 *                                                    host page (attach=avatar)
 *
 * The panel turns it on with ?cobrowse=1 (avatar-panel-v2.js forwards it and
 * loads the page engine). It only becomes active when the page engine has
 * answered from the host origin the loader named AND the agent has co-browse
 * on for that origin. Otherwise the panel runs a normal avatar session.
 *
 * Page loads: the host page keeps a record of the live call. On pagehide the
 * panel leaves the room quietly, the agent holds the call for 60s, and the
 * panel on the next page rejoins as the same identity (liveavatar-user) with
 * the resume key. The LiveAvatar keeps running in the room meanwhile, so the
 * new panel simply subscribes to its video again.
 *
 * With a LiveAvatar speaking for the agent the worker cannot tell when speech
 * has finished playing, so page-changing commands arrive with after_speech and
 * are held here until the avatar falls quiet (waitForSpeechEnd).
 */

export const COBROWSE_TOPIC = 'braincx.cobrowse';
const HOST_SOURCE = 'bcx-cobrowse-host';
const WIDGET_SOURCE = 'bcx-cobrowse-widget';
// Under the agent's 60s hold (REJOIN_GRACE_SECONDS in app/agent/cobrowse.py)
export const COBROWSE_RESUME_WINDOW_MS = 55 * 1000;
const HELLO_INTERVAL_MS = 1500;
const HELLO_MAX_TRIES = 20;
// How long a page change may wait for the avatar to finish speaking. Under
// PAGE_SPEECH_WAIT_SECONDS (app/agent/cobrowse.py), which the agent adds to
// that command's timeout.
const SPEECH_WAIT_MAX_MS = 7000;
// How long to wait for the page engine to say hello before starting a call
// without co-browse.
const HOST_WAIT_MS = 1500;

const encode = (obj) => new TextEncoder().encode(JSON.stringify(obj));

// How long a rejoined room may look empty before the call counts as over.
// Right after connect the agent and avatar can still be on their way in.
export const COBROWSE_REJOIN_SETTLE_MS = 2500;

/** Resolves true once the room has someone else in it, false after `ms`. */
export const waitForParticipant = (room, ms = COBROWSE_REJOIN_SETTLE_MS) => new Promise((resolve) => {
  if (room.remoteParticipants.size > 0) { resolve(true); return; }
  let timer = null;
  const done = (value) => {
    clearTimeout(timer);
    room.off(RoomEvent.ParticipantConnected, onJoin);
    resolve(value);
  };
  const onJoin = () => done(true);
  timer = setTimeout(() => done(room.remoteParticipants.size > 0), ms);
  room.on(RoomEvent.ParticipantConnected, onJoin);
});

// LiveKit caps a reliable data message at about 15 KB. A link-heavy page can
// pass that, so trim the snapshot (least useful parts first) to stay under.
// Same rules as fitPage in CobrowseWidget.js.
const MAX_PAGE_BYTES = 12000;
const fitPage = (page) => {
  if (!page) return page;
  const p = { ...page };
  const size = () => encode(p).length;
  if (size() <= MAX_PAGE_BYTES) return p;
  p.text = (p.text || '').slice(0, 400);
  p.on_screen = (p.on_screen || '').slice(0, 600);
  while (size() > MAX_PAGE_BYTES && (p.links || []).length > 12) {
    p.links = p.links.slice(0, Math.floor(p.links.length * 0.7));
  }
  if (size() > MAX_PAGE_BYTES) p.buttons = (p.buttons || []).slice(0, 15);
  if (size() > MAX_PAGE_BYTES) p.headings = (p.headings || []).slice(0, 12);
  if (size() > MAX_PAGE_BYTES) p.forms = (p.forms || []).slice(0, 2);
  // Still over: a message past the limit is dropped whole, and the agent loses
  // the page entirely. Trim inside the forms, then whatever is left.
  if (size() > MAX_PAGE_BYTES) {
    p.forms = (p.forms || []).map((f) => ({
      ...f,
      fields: (f.fields || []).slice(0, 15).map((fl) => (fl.options ? { ...fl, options: fl.options.slice(0, 8) } : fl)),
    }));
  }
  if (size() > MAX_PAGE_BYTES) { p.sections = []; p.embeds = []; p.text = ''; }
  if (size() > MAX_PAGE_BYTES) p.forms = [];
  if (size() > MAX_PAGE_BYTES) { p.links = (p.links || []).slice(0, 5); p.on_screen = ''; }
  return p;
};

/**
 * @param api            axios instance for the BrainCX API
 * @param agentId        agent id
 * @param requested      the panel was loaded with ?cobrowse=1
 * @param claimedOrigin  host origin the loader put in the URL (hostOrigin)
 * @param onResume       (record) => void, a released call to pick up
 * @param waitForSpeechEnd () => Promise, resolves once the avatar is quiet
 */
export default function useCobrowseBridge({ api, agentId, requested, claimedOrigin, onResume, waitForSpeechEnd }) {
  const framed = typeof window !== 'undefined' && window.parent && window.parent !== window;
  const enabled = !!(requested && framed && agentId);

  const [available, setAvailable] = useState(enabled ? null : false); // null = checking
  const [hostReady, setHostReady] = useState(false);
  const [controlStopped, setControlStopped] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);

  const hostOriginRef = useRef(null);
  const roomRef = useRef(null);
  const recordRef = useRef(null);
  const latestPageRef = useRef(null);
  const controlStoppedRef = useRef(false);
  const helloTimerRef = useRef(null);
  const leavingRef = useRef(false);
  const pendingRecordRef = useRef(null);
  const onResumeRef = useRef(onResume);
  const waitRef = useRef(waitForSpeechEnd);
  const availableRef = useRef(available);
  const hostReadyRef = useRef(false);
  onResumeRef.current = onResume;
  waitRef.current = waitForSpeechEnd;
  availableRef.current = available;

  const active = enabled && available === true && hostReady;

  // ── Host page messaging ──────────────────────────────────────────────────
  const postHost = useCallback((msg) => {
    if (!framed) return;
    // The call record (with its resume key) only ever goes to the verified host
    if (msg.type !== 'ready' && !hostOriginRef.current) return;
    try {
      window.parent.postMessage({ ...msg, source: WIDGET_SOURCE }, hostOriginRef.current || '*');
    } catch (_) { /* host page went away */ }
  }, [framed]);

  const publish = useCallback(async (msg) => {
    const room = roomRef.current;
    if (!room || room.state !== 'connected') return;
    try {
      const out = msg.page ? { ...msg, page: fitPage(msg.page) } : msg;
      await room.localParticipant.publishData(encode(out), { reliable: true, topic: COBROWSE_TOPIC });
    } catch (_) { /* the room is going away */ }
  }, []);

  const saveRecord = useCallback((patch) => {
    if (!recordRef.current && !patch?.roomName) return;
    recordRef.current = { ...(recordRef.current || {}), ...patch };
    postHost({ type: 'store', record: recordRef.current });
  }, [postHost]);

  const clearRecord = useCallback(() => {
    recordRef.current = null;
    postHost({ type: 'clear' });
    postHost({ type: 'call_state', active: false });
  }, [postHost]);

  // ── Is co-browse on for this agent and this site ─────────────────────────
  useEffect(() => {
    if (!enabled) { setAvailable(false); return undefined; }
    let cancelled = false;
    api.get(`/cobrowse/config/${agentId}`, { params: { origin: claimedOrigin || undefined } })
      .then((r) => {
        if (!cancelled) setAvailable(Boolean(r.data?.enabled && r.data?.origin_allowed && r.data?.avatar));
      })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; };
  }, [api, enabled, agentId, claimedOrigin]);

  // A call released by the previous page, held until the config check is in
  useEffect(() => {
    if (!active || !pendingRecordRef.current) return;
    const record = pendingRecordRef.current;
    pendingRecordRef.current = null;
    onResumeRef.current?.(record);
  }, [active]);

  // ── Hello to the agent (repeated until acknowledged) ─────────────────────
  const stopHello = useCallback(() => {
    clearInterval(helloTimerRef.current);
    helloTimerRef.current = null;
  }, []);

  const startHello = useCallback(() => {
    stopHello();
    let tries = 0;
    const send = () => {
      tries += 1;
      if (tries > HELLO_MAX_TRIES) { stopHello(); return; }
      const room = roomRef.current;
      publish({
        type: 'hello',
        page: latestPageRef.current,
        audio_ok: room ? room.canPlaybackAudio : true,
        control_stopped: controlStoppedRef.current,
      });
    };
    send();
    helloTimerRef.current = setInterval(send, HELLO_INTERVAL_MS);
  }, [publish, stopHello]);

  // ── Messages from the page engine ────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    const onMessage = (event) => {
      if (event.source !== window.parent) return;
      const d = event.data;
      if (!d || d.source !== HOST_SOURCE) return;
      // The first answer fixes the host origin; it must match what the loader
      // put in our URL. Everything after must come from that same origin.
      if (!hostOriginRef.current) {
        if (claimedOrigin && event.origin !== claimedOrigin) return;
        hostOriginRef.current = event.origin;
      } else if (event.origin !== hostOriginRef.current) {
        return;
      }

      switch (d.type) {
        case 'init':
          hostReadyRef.current = true;
          setHostReady(true);
          if (d.page) latestPageRef.current = d.page;
          controlStoppedRef.current = !!d.control_stopped;
          setControlStopped(!!d.control_stopped);
          if (d.record) {
            if (availableRef.current === true) onResumeRef.current?.(d.record);
            else if (availableRef.current === null) pendingRecordRef.current = d.record;
          }
          break;
        case 'result':
          publish({ type: 'result', id: d.id, ok: d.ok, error: d.error, message: d.message, data: d.data, page: d.page });
          break;
        case 'snapshot':
          if (d.page) latestPageRef.current = d.page;
          publish({ type: 'snapshot', page: d.page, reason: d.reason });
          break;
        case 'control':
          controlStoppedRef.current = !!d.stopped;
          setControlStopped(!!d.stopped);
          publish({ type: 'control', stopped: !!d.stopped });
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    postHost({ type: 'ready' });
    return () => window.removeEventListener('message', onMessage);
  }, [enabled, claimedOrigin, postHost, publish]);

  /** Wait briefly for the page engine, so a fast first click still gets co-browse. */
  const whenHostReady = useCallback(async () => {
    if (!enabled) return false;
    const deadline = Date.now() + HOST_WAIT_MS;
    while (Date.now() < deadline && (!hostReadyRef.current || availableRef.current === null)) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 100));
    }
    return hostReadyRef.current && availableRef.current === true;
  }, [enabled]);

  // ── Room wiring (before connect) ─────────────────────────────────────────
  const bindRoom = useCallback((room) => {
    roomRef.current = room;
    leavingRef.current = false;

    room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
      if (topic !== COBROWSE_TOPIC) return;
      let data;
      try { data = JSON.parse(new TextDecoder().decode(payload)); } catch (_) { return; }
      if (data.type === 'command') {
        const command = { id: data.id, action: data.action, args: data.args || {} };
        if (data.after_speech && waitRef.current) {
          // Let "one moment, I'll take you there" finish before the page goes
          const cap = new Promise((r) => setTimeout(r, SPEECH_WAIT_MAX_MS));
          Promise.race([waitRef.current(), cap]).catch(() => {}).then(() => {
            if (roomRef.current === room) postHost({ type: 'command', command });
          });
        } else {
          postHost({ type: 'command', command });
        }
      } else if (data.type === 'hello_ack') {
        stopHello();
      }
    });

    room.on(RoomEvent.ParticipantConnected, () => startHello());

    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      setAudioBlocked(!room.canPlaybackAudio);
      publish({ type: 'audio', ok: room.canPlaybackAudio });
    });

    room.on(RoomEvent.Disconnected, () => {
      stopHello();
      setAudioBlocked(false);
      if (roomRef.current === room) roomRef.current = null;
      if (leavingRef.current) return; // page load: the next page picks the call up
      clearRecord();
    });
  }, [postHost, publish, startHello, stopHello, clearRecord]);

  // ── After connect ────────────────────────────────────────────────────────
  const afterConnect = useCallback((room, { pillSide } = {}) => {
    setAudioBlocked(!room.canPlaybackAudio);
    if (room.remoteParticipants.size > 0) startHello();
    postHost({ type: 'call_state', active: true, pill_side: pillSide });
  }, [postHost, startHello]);

  const setPillSide = useCallback((pillSide) => {
    if (!recordRef.current) return;
    postHost({ type: 'call_state', active: true, pill_side: pillSide });
  }, [postHost]);

  // ── Start / rejoin ───────────────────────────────────────────────────────
  const start = useCallback(async (extra = {}) => {
    postHost({ type: 'snapshot_request' });
    const res = await api.post('/cobrowse/start', {
      agent_id: agentId,
      host_origin: hostOriginRef.current || claimedOrigin || null,
      page_url: latestPageRef.current?.url || null,
      ui: 'avatar',
      ...extra,
    });
    const started = res.data;
    recordRef.current = null;
    saveRecord({
      active: true,
      roomName: started.room_name,
      sessionId: started.session_id,
      resumeKey: started.resume_key,
      startedAt: Date.now(),
      captions: [],
      releasedAt: null,
    });
    return started;
  }, [api, agentId, claimedOrigin, postHost, saveRecord]);

  /** True if the record is a call this page may pick up. */
  const canResume = (record) =>
    !!(record?.roomName && record.releasedAt && Date.now() - record.releasedAt <= COBROWSE_RESUME_WINDOW_MS);

  const rejoin = useCallback(async (record) => {
    // Claim it, so a second tab or a reload cannot pick the same call up
    recordRef.current = { ...record, releasedAt: null };
    postHost({ type: 'store', record: recordRef.current });
    const res = await api.post('/cobrowse/rejoin', {
      session_id: record.sessionId,
      room_name: record.roomName,
      resume_key: record.resumeKey,
      host_origin: hostOriginRef.current || claimedOrigin || null,
    });
    return res.data;
  }, [api, claimedOrigin, postHost]);

  // ── End ──────────────────────────────────────────────────────────────────
  /** A hang-up, not a page load: the agent ends the call at once. */
  const hangup = useCallback(async () => {
    const room = roomRef.current;
    stopHello();
    if (recordRef.current) clearRecord();
    if (!room || room.state !== 'connected') return;
    await Promise.race([
      room.localParticipant.publishData(encode({ type: 'hangup' }), { reliable: true, topic: COBROWSE_TOPIC }),
      new Promise((r) => setTimeout(r, 500)),
    ]).catch(() => {});
  }, [clearRecord, stopHello]);

  const resumeAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try { await room.startAudio(); } catch (_) { /* still blocked */ }
    setAudioBlocked(!room.canPlaybackAudio);
    publish({ type: 'audio', ok: room.canPlaybackAudio });
  }, [publish]);

  // ── Hand the call on when the page goes away ─────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    const onPageHide = () => {
      const room = roomRef.current;
      if (!room || !recordRef.current) return;
      // Leave quietly (no hang-up message) so the agent holds the call while
      // the next page loads. The page engine marks the call record released.
      leavingRef.current = true;
      stopHello();
      room.disconnect();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [enabled, stopHello]);

  useEffect(() => () => clearInterval(helloTimerRef.current), []);

  return {
    requested: enabled,
    active,
    available,
    controlStopped,
    audioBlocked,
    leavingRef,
    whenHostReady,
    canResume,
    bindRoom,
    afterConnect,
    setPillSide,
    start,
    rejoin,
    saveRecord,
    clearRecord,
    hangup,
    resumeAudio,
    hasCall: () => !!recordRef.current,
  };
}
